<!--
  PurchaseRemitFormModal: alta y edición de remitos de compra, libres o desde una orden de compra.

  ENTRADA
    Props: no tiene.
    Métodos públicos (por ref):
      add()                    Abre el alta normal.
      addFromPurchaseOrder()   Abre el alta desde una orden de compra pendiente del proveedor.
      edit(item)               Abre la edición. item: remito de la API; el detalle se busca con mostrarRemitos(item.id).
      close()                  Cierra el modal sin confirmar.

  SALIDA
    Eventos:
      @saved(response)   Se guardó. La vista recarga lo que corresponda.
                         NO se emite si está marcado "Cargar la factura de este remito ahora": en ese caso
                         el propio modal navega a PurchaseStaffPurchaseGenerateByRemits.
    Efectos:
      Alta y edición por agregarRemitos y editarRemitos. GENERA MOVIMIENTOS REALES DE STOCK según el backend.

  USOS
    Vista: <PurchaseRemitFormModal ref="purchaseRemitForm" @saved="filterPurchaseRemits()" />
    Alta:        this.$refs.purchaseRemitForm.add()
    Desde OC:    this.$refs.purchaseRemitForm.addFromPurchaseOrder()
    Edición:     this.$refs.purchaseRemitForm.edit(item)
    Hoy se invoca desde purchase-remits/staff/purchaseRemits.vue: botones, dropdown, drawer y ?new=1&mode=from_order de la paleta.

  DEPENDENCIAS Y PERMISOS
    Componentes: FindObject, cpbtDetailDeposit/detail, v-select (global).
    Servicios: los del módulo y productPricing (vínculo del proveedor en costos).
    No controla permisos: quien lo invoca decide si el usuario puede abrirlo.
-->
<template>
  <div>
    <!-- CRUD MAIN -->
    <b-modal v-model="modal.form.active"
            no-close-on-esc
            no-close-on-backdrop
            hide-header-close
            size="xl"
            dialog-class="purchase-remits-form-modal-dialog"
            :key="modal.form._id"
            :id="modal.form._id">
      <div slot="modal-header" minimize class="app-modal-header">
        <span class="app-modal-header__icon"><AppIcon name="edit" /></span>
        <h5 class="app-modal-header__title">{{this.modal.form.title}}</h5>
      </div>

      <form-wizard  title=""
                    subtitle=""
                    nextButtonText="Siguiente"
                    backButtonText="Atrás"
                    finishButtonText="Guardar"
                    color="#2f353a"
                    shape="tab"
                    @on-complete="save"
                    class="crud-sales-wizard">

        <tab-content title="General" icon="fa fa-edit" :before-change="purchaseRemitsValidDetailGeneral">          
          <div class="purchase-remits-form-section">
            <div class="purchase-remits-form-section__title">
              <AppIcon name="description" /> Datos generales
            </div>
          <b-row v-if="crud.form.source_mode == 'from_order'" class="mb-2">
            <b-col md="12">
              <div class="app-alert app-alert--info">
                <span class="app-alert__icon"><AppIcon name="info" /></span>
                <div class="app-alert__content" v-if="crud.form.id">
                  Origen: orden de compra #{{crud.form.purchase_orders_id}}. La orden de origen no se modifica durante la edición.
                </div>
                <div class="app-alert__content" v-else>Modo de carga: remito desde orden de compra</div>
              </div>
            </b-col>
          </b-row>
          <b-row>      
            <b-col md="4">
              <b-form-group label="Fecha">
                <b-form-datepicker size="sm" v-model="crud.form.date" placeholder="Fecha" local="es"></b-form-datepicker>                      
              </b-form-group>            
            </b-col>           
          </b-row>    
          <b-row> 
            <b-col md="5">
              <FindObject render="search"
                          type="suppliers" 
                          @select-object="loadSuppliers($event)" 
                          :valueID="crud.form.suppliers_id"
                          :where="conditionSuppliersActive"
                          :disabled="crud.form.id>0" />                 
            </b-col>                                                                                             
            <b-col md="3"> 
              <FindObject tag="Punto de Venta (Imputación)"
                          render="search"
                          type="erpPointsSales" 
                          display="full_identification"
                          @select-object="loadPointsSales($event)" 
                          :valueID="crud.form.points_sales_id"
                          :key="'finder_point_sale_'+componentKey"
                          auto-select-single
                          :disabled="crud.form.id>0" 
                          :where="conditionPointsSalesActive" />                 
            </b-col>                                         
          </b-row>
          <b-row> 
            <b-col md="3">
              <b-form-group label="Comprobante">
                <div class="purchase-remits-voucher-type">
                  <span class="purchase-remits-voucher-type__icon"><AppIcon name="receipt" /></span>
                  <span>
                    <strong>Remito</strong>
                    <small>Ingreso de mercadería</small>
                  </span>
                </div>
              </b-form-group>
            </b-col>                
            <b-col md="2">
              <b-form-group label="Punto de Venta">    
                <app-text-input v-model.number="crud.form.point_sale"
                                type="text"
                                inputmode="numeric"
                                :allowed-pattern="/[0-9]/g"
                                size="sm"
                                maxlength="6" />
              </b-form-group>                            
            </b-col>  
            <b-col md="3">
              <b-form-group label="Número">    
                <app-text-input v-model.number="crud.form.number"
                                type="text"
                                inputmode="numeric"
                                :allowed-pattern="/[0-9]/g"
                                size="sm"
                                maxlength="10" />
              </b-form-group>                            
            </b-col>                         
          </b-row>             
          </div>
        </tab-content>

        <tab-content title="Orden de Compra"
                    icon="fa fa-file-text-o"
                    v-if="crud.form.source_mode == 'from_order' && !crud.form.id"
                    :before-change="purchaseRemitsValidDetailPurchaseOrder">
          <div class="purchase-remits-form-section">
            <div class="purchase-remits-form-section__title">
              <AppIcon name="cart" /> Orden de Compra
            </div>
          <b-row>
            <b-col md="12">
              <b-form-group label="Ordenes de compra pendientes del proveedor">
                <v-select :options="arr.purchaseOrdersPending"
                          v-model="crud.form.purchase_order"
                          placeholder="Seleccionar orden de compra"
                          :multiple="false"
                          :select-on-tab="true"
                          @input="loadPurchaseOrderToRemit($event)" />
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col md="12">
              <div class="app-alert app-alert--success" v-if="crud.form.purchase_orders_id">
                <span class="app-alert__icon"><AppIcon name="check2-circle" /></span>
                <div class="app-alert__content">OC seleccionada: <b>{{getPurchaseOrderLabel(crud.form.purchase_order)}}</b></div>
              </div>
              <div class="app-alert app-alert--warning" v-else>
                <span class="app-alert__icon"><AppIcon name="exclamation-triangle" /></span>
                <div class="app-alert__content">Debe seleccionar una orden de compra pendiente para precargar el detalle del remito.</div>
              </div>
            </b-col>
          </b-row>
          <b-row v-if="crud.form.purchase_orders_id">
            <b-col md="12">
              <b-table class="app-table app-table--compact table-purchase-remits-from-order"
                       responsive="sm"
                       :stacked="isMobileViewport ? 'md' : false"
                       :small="true"
                       :fixed="false"
                       :items="purchaseOrderGridItems"
                       :fields="purchaseOrderGridFields">

                <template v-slot:cell(item)="data">
                  <div class="purchase-remits-detail-item">
                    <b>{{getPurchaseOrderGridItemCode(data.item.detail)}}</b>
                    <span class="ml-1">{{getPurchaseOrderGridItemName(data.item.detail)}}</span>
                  </div>
                </template>

                <template v-slot:cell(quantity)="data">
                  <b-input-group size="sm">
                    <b-form-input v-model.number="data.item.detail.quantity"
                                  type="number"
                                  min="0"
                                  step="0.0001"
                                  @change="normalizePurchaseOrderDetailQuantity(data.item.detail)" />
                    <template #append>
                      <b-input-group-text>{{getPurchaseOrderGridItemUnit(data.item.detail)}}</b-input-group-text>
                    </template>
                  </b-input-group>
                </template>

                <template v-slot:cell(location)="data">
                  <b-form-select size="sm"
                                 v-model="data.item.detail.location_id"
                                 :options="arr.purchaseOrderLocationOptions"
                                 @change="setPurchaseOrderItemLocation(data.item.detail, $event)" />
                </template>

              </b-table>
            </b-col>
          </b-row>
          <b-row v-if="crud.form.purchase_orders_id && !purchaseOrderGridItems.length">
            <b-col md="12">
              <div class="app-alert app-alert--warning">
                <span class="app-alert__icon"><AppIcon name="exclamation-triangle" /></span>
                <div class="app-alert__content">La orden seleccionada no posee ítems pendientes para remitir.</div>
              </div>
            </b-col>
          </b-row>
          </div>
        </tab-content>

        <tab-content title="Detalle" icon="fa fa-list" :before-change="purchaseRemitsValidDetailItems">          
          <div class="purchase-remits-form-section">
            <div class="purchase-remits-form-section__title">
              <AppIcon name="list-ul" /> Productos cargados
            </div>
          <b-row>
            <b-col lg="12" md="12" sm="12">                                  
              <div v-if="!detailFormReady" class="text-center text-muted py-4">
                <b-spinner small class="mr-2" />Cargando detalle del remito...
              </div>
              <Detail v-else
                      @load-detail-products="setDetailItems($event, 'products')"
                      @load-detail-products-compound="setDetailItems($event, 'productsCompound')"
                      :operation="crud.form"
                      :detailProducts="detailProductsForDetailTab"
                      :detailProductsCompound="detailProductsCompoundForDetailTab"
                      :allowPurchasePricing="true"
                      :currency="getCurrency"
                      :allowMultipleProducts="true"
                      :allowInlineDetailEdit="true"
                      :key="'detail_' + componentKey"
                      :forceUpdate="componentKey" />                                             
            </b-col>        
          </b-row>
          </div>
        </tab-content>

        <tab-content title="Observaciones" icon="fa fa-flag-checkered" :key="'tab_f_'+componentKey">          
          <div class="purchase-remits-form-section">
            <div class="purchase-remits-form-section__title">
              <AppIcon name="chat-square-text" /> Observaciones
            </div>
          <b-row>
            <b-col md="12">
              <b-form-group label="Observaciones">
                <app-textarea
                  v-model="crud.form.observations"
                  placeholder="Ingresar las observaciones..."
                  rows="3"
                  max-rows="6"
                ></app-textarea>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row v-if="!crud.form.id">
            <b-col md="12">
              <div class="app-alert purchase-remits-invoice-toggle" :class="loadInvoiceAfterSave ? 'app-alert--success' : 'app-alert--info'">
                <span class="app-alert__icon"><AppIcon name="receipt" /></span>
                <div class="app-alert__content">
                  <b-form-checkbox v-model="loadInvoiceAfterSave" switch class="mb-0 font-weight-bold">
                    Cargar la factura de este remito ahora
                  </b-form-checkbox>
                  <div class="mt-1" v-if="loadInvoiceAfterSave">
                    Al generar el remito, se abre directamente la carga de la factura de compra con estos datos precargados.
                  </div>
                </div>
              </div>
            </b-col>
          </b-row>
          </div>
        </tab-content>

      </form-wizard>
      
      <div slot="modal-footer">
        <b-button variant="outline-secondary" class="mr-1" @click="cancel()">
          <AppIcon name="x" class="mr-1" />Cancelar
        </b-button>
      </div>                
    </b-modal>
  </div>
</template>
<script>
  import serviceAPI from '../services'
  import costsServiceAPI from '@/components/inc/productPricing/services'
  import Error from '@/handler/error'
  import Helper from '@/handler/helper'
  import FindObject from '@/components/inc/find/findObject'
  import moment from 'moment'
  import Detail from '@/components/inc/cpbtDetailDeposit/detail.vue'
  import {FormWizard, TabContent} from 'vue-form-wizard'
  import 'vue-form-wizard/dist/vue-form-wizard.min.css'

  export default {
    name: 'PurchaseRemitFormModal',
    components: {
      FindObject,
      Detail,
      FormWizard,
      TabContent,
    },
    data: () => {
      return {
        purchaseOrderGridFields: [
          {key: 'item', label: 'Producto', class: "align-middle"},
          {key: 'quantity', label: 'Cantidad', class: "align-middle text-center", thStyle: {width: '130px'}},
          {key: 'location', label: 'Ubicación', class: "align-middle", thStyle: {width: '200px'}},
        ],
        crud: {
          form: {
            id: 0,
            date: '',
            types_vouchers_id: 0,
            types_vouchers: null,
            letter: '',
            point_sale: 0,
            number: 0,
            observations: '',            
            staff_id: 0,              
            suppliers_id: 0,
            suppliers: null,      
            points_sales_id: 0,
            points_sales: null, 
            selectTypeVoucher: 'remito',
            reference_voucher_id: 0,                                      
            source_mode: 'normal',
            purchase_orders_id: 0,
            purchase_order: null,
            detailProducts: [],
            detailProductsCompound: [],
            type: {
              type_balance: '+',
            }
          },
        },
        modal: {
          form: {
            active: false,
            title: '',
          },
        },
        arr: {
          purchaseOrdersPending: [],
          purchaseOrderLocations: [],
          purchaseOrderLocationOptions: [],
        },
        componentKey: 0,
        isMobileViewport: false,
        detailFormReady: true,
        loadInvoiceAfterSave: false,
        purchaseOrderZeroCache: {
          products: [],
          productsCompound: [],
        },
      }
    },
    computed: {
      // CONDITIONS SELECT
      conditionSuppliersActive(){
        return [{field: 'active', condition: true}, {field: 'shipping', condition: false}];
      },
      conditionPointsSalesActive() {
        return [{field: 'active', condition: true}];
      },        

      getCurrency(){
        return "ARS"
      },

      purchaseOrderGridItems() {
        var items = []
        this.crud.form.detailProducts.forEach((element) => {
          items.push({ type: 'product', detail: element })
        })
        this.crud.form.detailProductsCompound.forEach((element) => {
          items.push({ type: 'compound', detail: element })
        })
        return items
      },
      detailProductsForDetailTab() {
        if(this.crud.form.source_mode != 'from_order') {
          return this.crud.form.detailProducts || []
        }
        return (this.crud.form.detailProducts || []).filter((item) => parseFloat(item.quantity || 0) > 0)
      },
      detailProductsCompoundForDetailTab() {
        if(this.crud.form.source_mode != 'from_order') {
          return this.crud.form.detailProductsCompound || []
        }
        return (this.crud.form.detailProductsCompound || []).filter((item) => parseFloat(item.quantity || 0) > 0)
      },
    },
    mounted() {
      this.mobileMql = window.matchMedia('(max-width: 767.98px)')
      this.isMobileViewport = this.mobileMql.matches
      this.mobileMql.addEventListener('change', this.updateMobileViewport)
    },
    beforeDestroy() {
      if(this.mobileMql) {
        this.mobileMql.removeEventListener('change', this.updateMobileViewport)
      }
    },
    watch: {
      'crud.form.suppliers_id': function () {                         
        this.searchVoucherBySupplier()
      },
      'crud.form.points_sales_id': function () {                        
        this.searchVoucherBySupplier()
      },
    },
    methods: {
      // API PUBLICA PARA LA VISTA
      close() {
        this.modal.form.active = false
      },

      updateMobileViewport(e) {
        this.isMobileViewport = e.matches
      },
      confirmDialog(message, options) {
        var danger = !!options.danger
        var icon = options.icon || (danger ? 'warning' : 'help')
        var title = this.$createElement('span', { class: 'app-confirm-header__title' }, [
          this.$createElement('AppIcon', { props: { name: icon } }),
          options.title
        ])
        return this.$bvModal.msgBoxConfirm(message, {
          title: title,
          size: options.size || 'sm',
          buttonSize: options.buttonSize || 'sm',
          okVariant: danger ? 'danger' : 'dark',
          okTitle: options.okTitle,
          cancelTitle: options.cancelTitle || 'Cancelar',
          footerClass: 'p-2',
          headerClass: danger ? 'app-confirm-header app-confirm-header--danger' : 'app-confirm-header',
          hideHeaderClose: false,
          centered: true
        })
      },
      forceRender() {
        this.componentKey += 1;  
      },

      // ABM MAIN
      add() {										
        this.crud.form.id = 0           
        this.crud.form.date = moment().format('YYYY-MM-DD')        
        this.crud.form.observations = ''
        this.crud.form.staff_id = Helper.getStaffId()        
        this.crud.form.suppliers_id = 0
        this.crud.form.suppliers = null                                   
        this.crud.form.types_vouchers_id = 0
        this.crud.form.types_vouchers = null                     
        this.crud.form.letter = ''
        this.crud.form.point_sale = 0
        this.crud.form.number = 0         
        this.crud.form.points_sales_id = 0
        this.crud.form.points_sales = null
        this.crud.form.source_mode = 'normal'
        this.crud.form.purchase_orders_id = 0
        this.crud.form.purchase_order = null
        
        this.crud.form.detailProducts = []
        this.crud.form.detailProductsCompound = []
        this.arr.purchaseOrdersPending = []
        this.purchaseOrderZeroCache.products = []
        this.purchaseOrderZeroCache.productsCompound = []
        this.detailFormReady = true
        this.loadInvoiceAfterSave = false

        this.modal.form._id = Math.random().toString(36).slice(2, 8)
        this.modal.form.active = true
        this.modal.form.title = 'Remito de Compra'
      },
      addFromPurchaseOrder() {
        this.add()
        this.crud.form.source_mode = 'from_order'
        this.modal.form.title = 'Remito de Compra desde Orden de Compra'
        this.loadAvailableLocations()
      },
      edit(item) {
        // Detail emite su estado inicial al montarse. Durante una edición se
        // espera la respuesta async antes de montarlo para que ese estado vacío
        // no pise los ítems recién cargados.
        this.detailFormReady = false
        this.crud.form.id = item.id
        this.crud.form.date = moment(item.date).format('YYYY-MM-DD')        
        this.crud.form.observations = item.observations
        this.crud.form.staff_id = item.staff_id
        this.crud.form.suppliers_id = item.suppliers_id
        this.crud.form.suppliers = item.supplier                   
        this.crud.form.types_vouchers_id = item.types_vouchers_id
        this.crud.form.types_vouchers = item.type_voucher
        this.crud.form.letter = item.letter
        this.crud.form.point_sale = item.point_sale
        this.crud.form.number = item.number
        this.crud.form.points_sales_id = item.points_sales_id
        this.crud.form.points_sales = item.points_sales
        this.crud.form.purchase_orders_id = item.purchase_orders_id ? parseInt(item.purchase_orders_id) : 0
        this.crud.form.purchase_order = null
        this.crud.form.source_mode = this.crud.form.purchase_orders_id ? 'from_order' : 'normal'
        this.arr.purchaseOrdersPending = []
        this.purchaseOrderZeroCache.products = []
        this.purchaseOrderZeroCache.productsCompound = []
        this.crud.form.detailProducts = []
        this.crud.form.detailProductsCompound = []
        
        var result = serviceAPI.mostrarRemitos(item.id);

        result.then((response) => {  
          var data = response.data

          if(data) {
            if(this.crud.form.source_mode == 'from_order') {
              this.loadAvailableLocations()
            }

            if(data.details.products) {
              this.crud.form.detailProducts = data.details.products.map((element) => {
                return this.mapPurchaseOrderDetailItem(element, 'product')
              })
            }            

            if(data.details.products_compound) {
              this.crud.form.detailProductsCompound = data.details.products_compound.map((element) => {
                return this.mapPurchaseOrderDetailItem(element, 'compound')
              })
            }

            if(data.purchase_order) {
              this.crud.form.purchase_order = {
                code: data.purchase_order.id,
                label: this.getPurchaseOrderLabel(data.purchase_order),
                data: data.purchase_order,
              }
            }

            this.detailFormReady = true
            this.forceRender()
          } else {
            this.detailFormReady = true
          }
        })
        .catch(error => {
          this.detailFormReady = true
          this.$awn.alert(Error.showError(error))
        })
                
        this.modal.form._id = Math.random().toString(36).slice(2, 8)
        this.modal.form.active = true
        this.modal.form.title = 'Editar Remito de Compra'
      },       
      save() {
        if(!this.crud.form.id && this.crud.form.source_mode == 'from_order' && !this.crud.form.purchase_orders_id) {
          this.$awn.alert('Debe seleccionar una orden de compra pendiente')
          return false
        }
        if(!this.crud.form.id && this.crud.form.source_mode == 'from_order' && !this.purchaseOrderGridItems.length) {
          this.$awn.alert('La orden de compra seleccionada no posee ítems para remitir')
          return false
        }

        var payload = this.getRemitPayloadForSave()
        if(this.crud.form.source_mode == 'from_order' &&
           !payload.detailProducts.length &&
           !payload.detailProductsCompound.length) {
          this.$awn.alert('Debe cargar al menos un ítem con cantidad mayor a cero')
          return false
        }

        if(this.loadInvoiceAfterSave) {
          // Mismo chequeo que generateInvoice()/hasRemitPrices(): si falta algún precio de
          // costo, la factura quedaría en $0 — se valida acá, antes de guardar, con los mismos
          // datos del payload (evita un viaje extra al servidor para volver a pedirlos).
          var allDetailItems = (payload.detailProducts || []).concat(payload.detailProductsCompound || [])
          var hasAllPrices = !allDetailItems.length || allDetailItems.every((item) => parseFloat(item.price_cost) > 0)
          if(!hasAllPrices) {
            this.$awn.alert('Hay ítems sin precio de costo. Completá los precios o desmarcá "Cargar la factura de este remito ahora" para continuar.')
            return false
          }
        }

        this.confirmDialog('¿Desea continuar con la generación del remito de compra?', {
          title: 'Generar Remito',
          icon: 'save',
          okTitle: 'GENERAR',
          cancelTitle: 'Cancelar'
        })
        .then(value => {
          if (value) {
            let loader = this.$loading.show();

            var isEdit = !!this.crud.form.id
            var result = null
            if(isEdit) {
              result = serviceAPI.editarRemitos(payload);
            } else {
              result = serviceAPI.agregarRemitos(payload);
            }

            result.then((response) => {
              this.modal.form.active = false
              loader.hide()

              if(!isEdit && this.loadInvoiceAfterSave && response.data && response.data.id) {
                this.$router.push({
                  name: 'PurchaseStaffPurchaseGenerateByRemits',
                  params: { remitsID: response.data.id },
                  query: { _t: Date.now() }
                })
                return
              }

              this.$emit('saved', response)
              this.$awn.success("Remito generado con éxito");
            })
            .catch(error => {
              loader.hide()
              this.$awn.alert(Error.showError(error));
            })
          }
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error));
        })              
      },  
      cancel() {
        this.confirmDialog('¿Desea cancelar el remito de compra?', {
          title: 'Cancelar el Remito',
          danger: true,
          okTitle: 'SI',
          cancelTitle: 'NO'
        })
        .then(value => {
          if (value) {        
            this.modal.form.active = false
          }
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error));
        })   
      },

      // ABM VALIDACIONES
      purchaseRemitsValidDetailGeneral() {               
        if( this.crud.form.suppliers_id && 
            this.crud.form.points_sales_id && 
            this.crud.form.types_vouchers_id && 
            this.crud.form.letter && 
            this.crud.form.point_sale && 
            this.crud.form.number) {          
          return true;
        } else {
          if(!this.crud.form.suppliers_id) {
            this.$awn.alert("No se cargó el proveedor");
            return false;
          }            

          if(!this.crud.form.points_sales_id) {
            this.$awn.alert("No se cargó el punto de venta de imputación");
            return false;
          }            

          if( !this.crud.form.types_vouchers_id ||
              !this.crud.form.letter || 
              !this.crud.form.point_sale ||
              !this.crud.form.number ) {                
            this.$awn.alert("Faltan datos del comprobante");
            return false;         
          }                       
        }        
      },
      purchaseRemitsValidDetailPurchaseOrder() {
        // La OC se elige una única vez al crear el remito. Al editar, los
        // renglones ya guardados conservan su purchase_orders_detail_id.
        if(this.crud.form.id || this.crud.form.source_mode != 'from_order') {
          return true
        }

        if(!this.crud.form.purchase_orders_id) {
          this.$awn.alert('Debe seleccionar una orden de compra pendiente')
          return false
        }

        if(this.purchaseOrderGridItems.length) {
          this.cachePurchaseOrderZeroDetails()
          this.forceRender()
          return true
        }

        this.$awn.alert('Debe cargar al menos un ítem con cantidad mayor a cero')
        return false
      },
      purchaseRemitsValidDetailItems() {
        if(!this.detailProductsForDetailTab.length && !this.detailProductsCompoundForDetailTab.length) {
          this.$awn.alert('No se cargó ningún ítem')
          return false
        }
        return this.verifySupplierLinkedItems()
        .then(() => {
          var unlinked = (this.crud.form.detailProducts || []).filter(item => item._supplierNotLinked)
          if(unlinked.length) {
            var names = unlinked.map(item => (item.product && item.product.name) ? item.product.name : 'Producto').join(', ')
            this.$awn.alert('Los siguientes productos no tienen al proveedor vinculado en costos: ' + names + '. Vinculalos desde el ícono de edición de precio antes de continuar.')
            return false
          }
          return true
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error))
          return false
        })
      },
      // El vínculo con el proveedor se resuelve en forma asíncrona al agregar cada
      // producto; los que siguen sin resolver (consulta en curso o fallida) se
      // verifican acá para no dejar pasar productos de otro proveedor.
      verifySupplierLinkedItems() {
        var supplierId = this.crud.form.suppliers_id
        var unresolved = (this.crud.form.detailProducts || []).filter((item) => {
          return item.product && item.product.source == 'product' && typeof item._supplierNotLinked !== 'boolean'
        })

        return Promise.all(unresolved.map((item) => {
          return costsServiceAPI.cargarProductoCostosPorId({ product_id: item.product.id })
          .then((response) => {
            var data = response.data || {}
            var list = Array.isArray(data.suppliers_products) ? data.suppliers_products : []
            this.$set(item, '_supplierNotLinked', !list.some(row => row.suppliers_id == supplierId))
          })
        }))
      },
      setDetailItems(event, type) {
        // Mantener la misma referencia de objeto para que actualizaciones async
        // de price_cost (hidratación) se reflejen en el payload al guardar
        var detail = Array.isArray(event) ? event.map((element) => {
          element.location_id = element.location ? element.location.id : 0
          return element
        }) : []

        if(this.crud.form.source_mode == 'from_order') {
          var cache = type=="products" ? this.purchaseOrderZeroCache.products : this.purchaseOrderZeroCache.productsCompound
          var current = type=="products" ? this.crud.form.detailProducts : this.crud.form.detailProductsCompound
          var zeroItems = Array.isArray(cache) && cache.length
            ? cache
            : (current || []).filter((item) => parseFloat(item.quantity || 0) <= 0)

          var detailKeys = {}
          detail.forEach((item) => {
            detailKeys[this.buildPurchaseOrderDetailKey(item, type)] = true
          })

          zeroItems.forEach((item) => {
            var itemCopy = Object.assign({}, item)
            itemCopy.location_id = itemCopy.location ? itemCopy.location.id : (itemCopy.location_id || 0)
            var key = this.buildPurchaseOrderDetailKey(itemCopy, type)
            if(!detailKeys[key]) {
              detail.push(itemCopy)
              detailKeys[key] = true
            }
          })
        }

        if(type=="products") {
          this.crud.form.detailProducts = detail                
        } 
        if(type=="productsCompound") {          
          this.crud.form.detailProductsCompound = detail                
        }              
      },   
      getPurchaseOrderGridItemCode(item) {
        if(item.productCompound && item.productCompound.code) {
          return item.productCompound.code
        }
        if(item.product && item.product.code) {
          return item.product.code
        }
        return '--'
      },
      getPurchaseOrderGridItemName(item) {
        if(item.productCompound && item.productCompound.name) {
          return item.productCompound.name
        }
        if(item.product && item.product.name) {
          var label = item.product.name
          var colorName = item.product.color && item.product.color.name ? item.product.color.name : ''
          var waistName = item.product.waist && item.product.waist.name ? item.product.waist.name : ''
          if(colorName && waistName) {
            label += ' (' + colorName + ' - ' + waistName + ')'
          } else if(colorName) {
            label += ' (' + colorName + ')'
          } else if(waistName) {
            label += ' (' + waistName + ')'
          }
          return label
        }
        return 'Sin descripción'
      },
      getPurchaseOrderGridItemUnit(item) {
        if(item.unit_measurement) {
          if(item.unit_measurement.reference) {
            return item.unit_measurement.reference
          }
          if(item.unit_measurement.name) {
            return item.unit_measurement.name
          }
        }
        return ''
      },
      buildPurchaseOrderLocationLabel(location) {
        if(!location || !location.zone || !location.zone.deposit) {
          return ''
        }
        var depositName = location.zone.deposit.name || location.zone.deposit.reference || ''
        if(location.full_reference && location.full_reference == depositName) {
          return depositName
        }
        return [depositName, location.zone.reference || location.zone.name, location.reference || location.name]
          .filter(Boolean)
          .join(' / ')
      },
      loadAvailableLocations(force=false) {
        if(this.arr.purchaseOrderLocations.length && !force) {
          return Promise.resolve(this.arr.purchaseOrderLocations)
        }

        return serviceAPI.obtenerUbicacionesDeposito({
          filter: '',
          where: [{ field: 'active', condition: true }],
          orWhere: [],
        })
        .then((response) => {
          var rows = Array.isArray(response.data) ? response.data : []
          this.arr.purchaseOrderLocations = rows
          this.arr.purchaseOrderLocationOptions = rows.map((location) => {
            return {
              value: location.id,
              text: this.buildPurchaseOrderLocationLabel(location),
            }
          })
          this.applyDefaultLocationToCurrentDetails()
          return rows
        })
      },
      applyDefaultLocationToCurrentDetails() {
        var defaultLocation = this.getDefaultPurchaseOrderLocation()
        if(!defaultLocation) {
          return
        }

        this.crud.form.detailProducts.forEach((item) => {
          if(!item.location || !item.location.id) {
            item.location = defaultLocation
            item.location_id = defaultLocation.id
          }
        })
        this.crud.form.detailProductsCompound.forEach((item) => {
          if(!item.location || !item.location.id) {
            item.location = defaultLocation
            item.location_id = defaultLocation.id
          }
        })
      },
      getDefaultPurchaseOrderLocation() {
        if(this.arr.purchaseOrderLocations && this.arr.purchaseOrderLocations.length) {
          return this.arr.purchaseOrderLocations[0]
        }
        return null
      },
      resolvePurchaseOrderItemLocation(location) {
        if(location && location.id) {
          var found = this.arr.purchaseOrderLocations.find((element) => parseInt(element.id) === parseInt(location.id))
          return found || location
        }
        return this.getDefaultPurchaseOrderLocation()
      },
      buildPurchaseOrderDetailKey(item, type='products') {
        var purchaseOrderDetailID = parseInt(item.purchase_orders_detail_id || 0)
        var locationID = parseInt(item.location_id || (item.location && item.location.id ? item.location.id : 0))
        var productID = 0

        if(type == 'productsCompound') {
          productID = parseInt(item.productCompound && item.productCompound.id ? item.productCompound.id : 0)
        } else {
          productID = parseInt(item.product && item.product.id ? item.product.id : 0)
        }

        return purchaseOrderDetailID + '|' + productID + '|' + locationID
      },
      cachePurchaseOrderZeroDetails() {
        this.purchaseOrderZeroCache.products = (this.crud.form.detailProducts || [])
          .filter((item) => parseFloat(item.quantity || 0) <= 0)
          .map((item) => Object.assign({}, item))

        this.purchaseOrderZeroCache.productsCompound = (this.crud.form.detailProductsCompound || [])
          .filter((item) => parseFloat(item.quantity || 0) <= 0)
          .map((item) => Object.assign({}, item))
      },
      normalizePurchaseOrderDetailQuantity(item) {
        var value = parseFloat(item.quantity)
        if(isNaN(value) || value < 0) {
          value = 0
        }
        item.quantity = value
      },
      getRemitPayloadForSave() {
        var payload = JSON.parse(JSON.stringify(this.crud.form))

        if(this.crud.form.source_mode == 'from_order') {
          payload.detailProducts = (payload.detailProducts || []).filter((item) => parseFloat(item.quantity || 0) > 0)
          payload.detailProductsCompound = (payload.detailProductsCompound || []).filter((item) => parseFloat(item.quantity || 0) > 0)
        }

        return payload
      },
      setPurchaseOrderItemLocation(item, locationId) {
        var location = this.arr.purchaseOrderLocations.find((element) => parseInt(element.id) === parseInt(locationId))
        item.location = location || null
        item.location_id = item.location ? item.location.id : 0
      },
      mapPurchaseOrderDetailItem(element, sourceType='product') {
        var location = this.resolvePurchaseOrderItemLocation(element.location)
        var item = {
          quantity: parseFloat(element.quantity || 0),
          quantityOld: parseFloat(element.quantity || 0),
          unit_measurement: element.unit_measurement,
          location: location,
          location_id: location ? location.id : 0,
          location_destination: element.location_destination,
          purchase_orders_id: element.purchase_orders_id,
          purchase_orders_detail_id: element.purchase_orders_detail_id,
        }

        if(sourceType == 'compound') {
          item.productCompound = element.productCompound || element.products_compound || element.product || null
        } else {
          item.product = element.product || null
        }

        return item
      },
      getPurchaseOrderLabel(item) {
        if(!item) {
          return ''
        }

        var source = item.data ? item.data : item
        var voucher = 'OC #' + source.id
        if(source.status_label) {
          voucher += ' | ' + source.status_label
        }
        if(source.quantity_pending) {
          voucher += ' | Pendiente: ' + parseFloat(source.quantity_pending).toFixed(2)
        }
        return voucher
      },
      loadPendingPurchaseOrders(resetSelection=true) {
        if(!this.crud.form.suppliers_id) {
          this.arr.purchaseOrdersPending = []
          if(resetSelection) {
            this.crud.form.purchase_order = null
            this.crud.form.purchase_orders_id = 0
            this.crud.form.detailProducts = []
            this.crud.form.detailProductsCompound = []
            this.forceRender()
          }
          return false
        }

        let params = {
          suppliers: [{ code: this.crud.form.suppliers_id }],
          status: [{ code: 'pending' }, { code: 'partial' }],
          date_start: moment().subtract(5, 'years').format('YYYY-MM-DD'),
          date_end: moment().add(1, 'day').format('YYYY-MM-DD'),
        }

        serviceAPI.obtenerOrdenesCompraPendientes(params, 1)
        .then((response) => {
          var data = response.data
          var rows = data && data.data ? data.data : []
          this.arr.purchaseOrdersPending = rows
            .filter((element) => parseFloat(element.quantity_pending || 0) > 0)
            .map((element) => {
              return {
                code: element.id,
                label: this.getPurchaseOrderLabel(element),
                data: element,
              }
            })

          if(resetSelection) {
            this.crud.form.purchase_order = null
            this.crud.form.purchase_orders_id = 0
            this.crud.form.detailProducts = []
            this.crud.form.detailProductsCompound = []
            this.forceRender()
          }
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error))
        })
      },
      loadPurchaseOrderToRemit(object) {
        if(!object) {
          this.crud.form.purchase_orders_id = 0
          this.crud.form.purchase_order = null
          this.crud.form.detailProducts = []
          this.crud.form.detailProductsCompound = []
          this.purchaseOrderZeroCache.products = []
          this.purchaseOrderZeroCache.productsCompound = []
          this.forceRender()
          return false
        }

        let loader = this.$loading.show()
        Promise.all([
          this.loadAvailableLocations(),
          serviceAPI.prearmarRemitoDesdeOrden(object.code, {
            date: this.crud.form.date,
            points_sales_id: this.crud.form.points_sales_id,
            types_vouchers_id: this.crud.form.types_vouchers_id,
          }),
        ])
        .then((response) => {
          var data = response[1].data
          this.crud.form.purchase_order = object
          this.crud.form.purchase_orders_id = object.code

          if(data.suppliers_id) {
            this.crud.form.suppliers_id = data.suppliers_id
          }
          if(data.points_sales_id && !this.crud.form.points_sales_id) {
            this.crud.form.points_sales_id = data.points_sales_id
          }
          if(data.types_vouchers_id && !this.crud.form.types_vouchers_id) {
            this.crud.form.types_vouchers_id = data.types_vouchers_id
          }
          if(data.letter && !this.crud.form.letter) {
            this.crud.form.letter = data.letter
          }
          this.crud.form.detailProducts = []
          if(data.detailProducts) {
            data.detailProducts.forEach((element) => {
              this.crud.form.detailProducts.push(this.mapPurchaseOrderDetailItem(element, 'product'))
            })
          }

          this.crud.form.detailProductsCompound = []
          if(data.detailProductsCompound) {
            data.detailProductsCompound.forEach((element) => {
              this.crud.form.detailProductsCompound.push(this.mapPurchaseOrderDetailItem(element, 'compound'))
            })
          }

          this.cachePurchaseOrderZeroDetails()
          this.forceRender()
          loader.hide()
        })
        .catch(error => {
          loader.hide()
          this.$awn.alert(Error.showError(error))
        })
      },
      
      // ABM FLUJO
      loadSuppliers (object) {        
        if(object){
          if(this.crud.form.suppliers_id != object.id) {
            this.forceRender()
          }

          this.crud.form.suppliers = object
          this.crud.form.suppliers_id = object.id   
                    
          if(object.data_fiscal) {
            if(object.data_fiscal.points_sales_id) {
              if(!this.crud.form.points_sales_id) {
                this.crud.form.points_sales = object.data_fiscal.points_sales
                this.crud.form.points_sales_id = object.data_fiscal.points_sales_id
              }
            }          
          }          

          // Al editar, FindObject emite select-object al precargar el proveedor:
          // no hay que buscar OCs pendientes ni resetear la OC/detalle ya cargados.
          if(this.crud.form.source_mode == 'from_order' && !this.crud.form.id) {
            this.loadPendingPurchaseOrders(true)
            this.loadAvailableLocations()
          }
        } else {
          this.crud.form.suppliers = null
          this.crud.form.suppliers_id = 0        
          
          this.crud.form.points_sales = null
          this.crud.form.points_sales_id = 0

          this.cleanDataVoucher()
          this.arr.purchaseOrdersPending = []
          this.crud.form.purchase_order = null
          this.crud.form.purchase_orders_id = 0
        }
      },   
      loadPointsSales (object) {                
        if(object){
          this.crud.form.points_sales = object
          this.crud.form.points_sales_id = object.id             
        } else {
          this.crud.form.points_sales = null
          this.crud.form.points_sales_id = 0
        }
      },                
      cleanDataVoucher() {
        this.arr.typeVouchers = null
        this.arr.select.typeVouchers = []

        this.crud.form.letter = ''
        this.crud.form.types_vouchers_id = 0
        this.crud.form.types_vouchers = null 
      },      

      // SELECT VOUCHER 
      searchVoucherBySupplier() {
        if(!this.crud.form.id) {
          if(this.crud.form.suppliers_id && this.crud.form.points_sales_id ) {
            var result = serviceAPI.obtenerVoucherBySupplier({
              'suppliers_id': this.crud.form.suppliers_id,
              'reference': this.crud.form.selectTypeVoucher,
              'point_sale_id': this.crud.form.points_sales_id,
            });
            result.then((response) => {    
              this.crud.form.types_vouchers_id = response.data[0].id							
              this.searchVoucher()                          
            })
            .catch(error => {
              this.$awn.alert(Error.showError(error));
            })   						           
          }
        }
      },			
      searchVoucher() {
        if(!this.crud.form.id) {
          if(this.crud.form.suppliers_id && this.crud.form.points_sales_id ) {
            var result = serviceAPI.obtenerVoucherById({
              'points_sales_id': this.crud.form.points_sales_id,
              'types_vouchers_id' : this.crud.form.types_vouchers_id,
            });
            result.then((response) => {                   
              this.crud.form.types_vouchers = response.data.type_voucher
              this.crud.form.letter = response.data.type_voucher.letter                            
            })
          }
        }
      },        
    }
  }
</script>
<style>
  .purchase-remits-detail-item {
    white-space: normal;
    line-height: 1.2;
  }
  .table-purchase-remits-from-order {
    overflow: auto;
    max-height: 320px;
  }
  .purchase-remits-form-modal-dialog {
    max-width: min(1400px, 95vw);
  }
  .purchase-remits-form-section {
    border: 1px solid var(--app-color-border);
    border-radius: 8px;
    background: var(--app-color-surface);
    padding: 14px;
  }
  .purchase-remits-form-section__title {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 12px;
    color: var(--app-color-text);
    font-size: var(--app-text-sm);
    font-weight: var(--app-font-semibold);
  }
  .purchase-remits-voucher-type {
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 31px;
    padding: 5px 9px;
    border: 1px solid #a6d8e8;
    border-radius: 4px;
    background: #edf9fc;
    color: #14566e;
    line-height: 1.05;
  }
  .purchase-remits-voucher-type__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #d3f0f8;
    color: #08759a;
  }
  .purchase-remits-voucher-type strong,
  .purchase-remits-voucher-type small {
    display: block;
  }
  .purchase-remits-voucher-type strong {
    font-size: 0.78rem;
    font-weight: var(--app-font-semibold);
    text-transform: uppercase;
    letter-spacing: 0.02em;
  }
  .purchase-remits-voucher-type small {
    margin-top: 2px;
    color: #4b7b8d;
    font-size: 0.68rem;
  }
  .crud-sales-wizard .wizard-card-footer {
    display: flex !important;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 14px 4px 0;
  }
  .crud-sales-wizard .wizard-card-footer .wizard-footer-left,
  .crud-sales-wizard .wizard-card-footer .wizard-footer-right {
    float: none !important;
  }
  .crud-sales-wizard .wizard-card-footer .wizard-footer-right {
    margin-left: auto;
  }
  .crud-sales-wizard .wizard-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 112px;
    height: 36px;
    padding: 6px 14px;
    border-width: 1px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: var(--app-font-semibold);
    box-shadow: none;
    transition: background-color .15s ease, border-color .15s ease, color .15s ease;
  }
  .crud-sales-wizard .wizard-footer-left .wizard-btn::before,
  .crud-sales-wizard .wizard-footer-right .wizard-btn::after {
    font-family: 'Material Symbols Rounded';
    font-size: 18px;
    font-weight: normal;
    font-style: normal;
    font-feature-settings: 'liga';
    line-height: 1;
  }
  .crud-sales-wizard .wizard-footer-left .wizard-btn::before {
    content: 'arrow_back';
    margin-right: 6px;
  }
  .crud-sales-wizard .wizard-footer-right .wizard-btn::after {
    content: 'arrow_forward';
    margin-left: 6px;
  }
  .crud-sales-wizard .wizard-footer-left .wizard-btn {
    background-color: var(--app-color-surface) !important;
    border-color: var(--app-color-border-strong) !important;
    color: var(--app-color-text-secondary) !important;
  }
  .crud-sales-wizard .wizard-footer-left .wizard-btn:hover {
    background-color: var(--app-color-surface-subtle) !important;
    color: var(--app-color-text) !important;
  }
  .crud-sales-wizard .wizard-footer-right .wizard-btn {
    background-color: var(--app-color-primary) !important;
    border-color: var(--app-color-primary) !important;
    color: #fff !important;
  }
  .crud-sales-wizard .wizard-footer-right .wizard-btn:hover {
    filter: brightness(.94);
  }
  @media (max-width: 767.98px) {
    .crud-sales-wizard .wizard-card-footer {
      gap: 8px;
      padding: 12px 0 0;
    }
    .crud-sales-wizard .wizard-footer-left,
    .crud-sales-wizard .wizard-footer-right {
      display: flex;
      flex: 1 1 0;
    }
    .crud-sales-wizard .wizard-card-footer .wizard-footer-right {
      margin-left: 0;
    }
    .crud-sales-wizard .wizard-footer-left > span,
    .crud-sales-wizard .wizard-footer-right > span {
      display: block;
      width: 100%;
    }
    .crud-sales-wizard .wizard-btn {
      width: 100%;
      min-width: 0;
    }
  }
</style>
